// Script Library — view all AI-generated scripts, copy or delete.
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Sparkles, Copy, Check, Trash2, Search, Wand2, X, Send } from "lucide-react";
import { apiGet, apiMutate, client, PLATFORMS } from "@/lib/api";
import ScriptModal from "@/components/ScriptModal";
import EmptyState from "@/components/EmptyState";

const INK_55 = "var(--ink-55)";
const INK_72 = "var(--ink-72)";
const SEAL = "var(--seal)";

function renderInline(s) {
  const parts = [];
  let rest = s;
  let key = 0;
  const re = /(\*\*[^*]+\*\*|\*[^*]+\*|\[[^\]]+\])/;
  while (rest) {
    const m = rest.match(re);
    if (!m) { parts.push(rest); break; }
    parts.push(rest.slice(0, m.index));
    const t = m[0];
    if (t.startsWith("**")) parts.push(<strong key={`b${key++}`} style={{ color: "var(--ink)" }}>{t.slice(2, -2)}</strong>);
    else if (t.startsWith("*")) parts.push(<em key={`i${key++}`} className="font-display-italic" style={{ color: "var(--ink)" }}>{t.slice(1, -1)}</em>);
    else parts.push(<span key={`k${key++}`} className="text-xs font-mono px-1.5 py-0.5 rounded" style={{ background: "rgba(216,168,87,0.12)", color: SEAL }}>{t.slice(1, -1)}</span>);
    rest = rest.slice(m.index + t.length);
  }
  return parts;
}

function FullScript({ text }) {
  if (!text) return null;
  const lines = text.split("\n");
  const blocks = [];
  let para = [];
  const flush = () => {
    if (para.length) {
      blocks.push(
        <p key={`p${blocks.length}`} className="text-sm leading-relaxed mb-3" style={{ color: INK_72 }}>
          {renderInline(para.join(" "))}
        </p>
      );
      para = [];
    }
  };
  lines.forEach((ln, idx) => {
    if (ln.startsWith("## ")) {
      flush();
      blocks.push(
        <h3 key={`h${idx}`} className="font-heading text-xl mt-5 mb-2" style={{ fontVariationSettings: "'opsz' 48, 'wght' 500", color: "var(--ink)" }}>
          {ln.slice(3)}
        </h3>
      );
    } else if (ln.startsWith("### ")) {
      flush();
      blocks.push(
        <h4 key={`h${idx}`} className="text-xs uppercase tracking-[0.22em] mt-4 mb-1.5" style={{ color: SEAL }}>
          {ln.slice(4)}
        </h4>
      );
    } else if (ln.trim() === "") {
      flush();
    } else {
      para.push(ln);
    }
  });
  flush();
  return <div>{blocks}</div>;
}

function ScriptViewerModal({ script, onClose, onDelete }) {
  const [copied, setCopied] = useState(false);
  const [sending, setSending] = useState(false);
  const navigate = useNavigate();
  const platform = PLATFORMS.find((p) => p.id === script.platform);
  useEffect(() => {
    const h = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [onClose]);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(script.content || "");
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { /* noop */ }
  };
  const sendToPipeline = async () => {
    if (sending) return;
    setSending(true);
    try {
      // Create a new pipeline idea pre-filled with the script content.
      // Stage = "script" so it lands in the right pipeline column.
      const created = await apiMutate("post", "/ideas", {
        title: script.topic || "Untitled script",
        description: `## Script (${script.platform}, ${script.tone})\n\n${script.content}`,
        platform: script.platform,
        platforms: [script.platform],
        stage: "script",
        tags: [],
      });
      toast.success("Sent to pipeline!", {
        description: `"${(script.topic || "Untitled").slice(0, 40)}" is now in Script stage.`,
        duration: 5000,
        action: {
          label: "Open pipeline",
          onClick: () => navigate("/pipeline"),
        },
      });
      onClose();
    } catch (e) {
      toast.error("Couldn't send to pipeline", { description: e?.response?.data?.detail || "Please try again." });
    } finally {
      setSending(false);
    }
  };
  return (
    <div
      className="fixed inset-0 z-[85] bg-black/70 backdrop-blur-sm flex items-end md:items-center justify-center"
      onClick={onClose}
      data-testid="script-viewer-backdrop"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="rounded-t-2xl md:rounded-2xl w-full md:max-w-3xl max-h-[94vh] overflow-y-auto"
        style={{ background: "var(--surface)", border: "1px solid var(--ink-22)" }}
        data-testid="script-viewer-modal"
      >
        <div
          className="flex items-center justify-between px-5 md:px-6 py-4 sticky top-0 z-10"
          style={{ background: "var(--surface)", borderBottom: "1px solid var(--ink-22)" }}
        >
          <div className="flex items-center gap-2.5 min-w-0">
            <Sparkles size={16} style={{ color: SEAL }} />
            <div className="min-w-0">
              <div className="eyebrow">{platform?.short || script.platform} · {script.tone} · {script.length}</div>
              <div className="font-heading text-base md:text-lg truncate" style={{ fontVariationSettings: "'opsz' 32, 'wght' 500" }}>
                {script.topic || "Untitled script"}
              </div>
            </div>
          </div>
          <button onClick={onClose} className="btn-ghost flex-shrink-0" data-testid="script-viewer-close" style={{ touchAction: "manipulation" }}>
            <X size={18} />
          </button>
        </div>
        <div className="px-5 md:px-6 py-5">
          <FullScript text={script.content} />
        </div>
        <div
          className="flex flex-col md:flex-row gap-2 px-5 md:px-6 py-4 sticky bottom-0"
          style={{ background: "var(--surface)", borderTop: "1px solid var(--ink-22)", paddingBottom: "calc(1rem + env(safe-area-inset-bottom, 0px))" }}
        >
          <button
            onClick={sendToPipeline}
            disabled={sending}
            className="btn-primary flex-1 md:flex-initial md:px-5 disabled:opacity-60"
            data-testid="script-viewer-send-pipeline"
            style={{ touchAction: "manipulation" }}
          >
            <Send size={14} /> {sending ? "Sending…" : "Send to Pipeline"}
          </button>
          <button
            onClick={copy}
            className="btn-secondary flex-1 md:flex-initial md:px-5"
            data-testid="script-viewer-copy"
            style={{ touchAction: "manipulation" }}
          >
            {copied ? <><Check size={14} /> Copied</> : <><Copy size={14} /> Copy</>}
          </button>
          <button
            onClick={() => onDelete(script)}
            className="btn-ghost text-xs md:text-sm md:px-3"
            style={{ color: "#c97a6a", touchAction: "manipulation" }}
            data-testid="script-viewer-delete"
          >
            <Trash2 size={14} /> Delete
          </button>
        </div>
      </div>
    </div>
  );
}

function ScriptCard({ script, onCopy, onDelete, onOpen, copied }) {
  const platform = PLATFORMS.find((p) => p.id === script.platform);
  const preview = (script.content || "").replace(/[#*]/g, "").slice(0, 220);
  return (
    <article
      className="card flex flex-col gap-3 group cursor-pointer transition hover:border-[var(--seal)]"
      data-testid={`script-card-${script.id}`}
      onClick={() => onOpen(script)}
      role="button"
      style={{ touchAction: "manipulation" }}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="eyebrow mb-2">{platform?.short || script.platform} · {script.tone}</div>
          <h3 className="font-heading text-lg leading-tight line-clamp-2" style={{ fontVariationSettings: "'opsz' 32, 'wght' 450" }}>
            {script.topic || "Untitled script"}
          </h3>
        </div>
        <div className="flex items-center gap-1.5 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
          <button
            onClick={() => onCopy(script)}
            className="btn-ghost text-xs"
            data-testid={`script-card-${script.id}-copy`}
            title="Copy script"
            style={{ touchAction: "manipulation" }}
          >
            {copied ? <Check size={12} /> : <Copy size={12} />}
            {copied ? "Copied" : "Copy"}
          </button>
          <button
            onClick={() => onDelete(script)}
            className="btn-ghost text-xs"
            style={{ color: "#c97a6a", touchAction: "manipulation" }}
            data-testid={`script-card-${script.id}-delete`}
            title="Delete"
          >
            <Trash2 size={12} />
          </button>
        </div>
      </div>
      <p className="text-xs leading-relaxed" style={{ color: INK_72 }}>
        {preview}
        {(script.content || "").length > 220 && "…"}
      </p>
      <div className="flex items-center justify-between text-[10px] font-mono pt-2" style={{ color: INK_55, borderTop: "1px solid var(--ink-22)" }}>
        <span>{(script.created_at || "").slice(0, 10)}</span>
        <span style={{ color: SEAL }}>Tap to open →</span>
      </div>
    </article>
  );
}

export default function Scripts() {
  const [scripts, setScripts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [copiedId, setCopiedId] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [viewing, setViewing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const data = await apiGet("/ai/scripts");
      setScripts(data || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const filtered = search.trim()
    ? scripts.filter((s) => (s.topic || "").toLowerCase().includes(search.toLowerCase()))
    : scripts;

  const onCopy = async (s) => {
    try {
      await navigator.clipboard.writeText(s.content || "");
      setCopiedId(s.id);
      setTimeout(() => setCopiedId(null), 1500);
    } catch { /* noop */ }
  };

  const onDelete = async (s) => {
    if (!window.confirm("Delete this script?")) return;
    await client.delete(`/ai/scripts/${s.id}`);
    if (viewing?.id === s.id) setViewing(null);
    load();
  };

  return (
    <div className="max-w-6xl mx-auto">
      <div className="flex items-end justify-between flex-wrap gap-3 mb-8">
        <div>
          <div className="eyebrow"><span className="seal-dot mr-2 align-middle" /> iv. Script Library</div>
          <h1 className="font-heading mt-4 text-4xl md:text-5xl" style={{ fontVariationSettings: "'opsz' 96, 'wght' 450" }}>
            Every script, <span className="font-display-italic">saved</span>.
          </h1>
          <p className="text-sm mt-3 max-w-xl" style={{ color: INK_72 }}>
            Every AI-generated script is auto-saved here. Copy with one tap, regenerate when needed, or delete what you don&apos;t need.
          </p>
        </div>
        <button onClick={() => setShowNew(true)} className="btn-primary" data-testid="scripts-new-btn">
          <Wand2 size={14} /> New script
        </button>
      </div>

      {scripts.length > 5 && (
        <div className="relative mb-6">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: INK_55 }} />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search scripts by topic…"
            className="input pl-10"
            data-testid="scripts-search-input"
          />
        </div>
      )}

      {loading ? (
        <div className="text-sm py-12 text-center" style={{ color: INK_55 }}>Loading library…</div>
      ) : scripts.length === 0 ? (
        <EmptyState
          icon={Sparkles}
          title="No scripts yet"
          body="Generate your first AI script from the dashboard, an idea card, or right here."
          primaryLabel="Write your first script"
          onPrimary={() => setShowNew(true)}
          testid="scripts-empty"
        />
      ) : filtered.length === 0 ? (
        <div className="text-sm py-12 text-center" style={{ color: INK_55 }}>No scripts match &ldquo;{search}&rdquo;.</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="scripts-grid">
          {filtered.map((s) => (
            <ScriptCard key={s.id} script={s} onCopy={onCopy} onDelete={onDelete} onOpen={setViewing} copied={copiedId === s.id} />
          ))}
        </div>
      )}

      {showNew && (
        <ScriptModal onClose={() => setShowNew(false)} onSaved={() => { setShowNew(false); load(); }} />
      )}
      {viewing && (
        <ScriptViewerModal script={viewing} onClose={() => setViewing(null)} onDelete={onDelete} />
      )}
    </div>
  );
}
